/**
 * Iris, as the tab and the Worker both need her (docs/iris.md).
 *
 * The one place that decides which mode a mission gets, the shapes of a step's
 * request and answer, and the helpers that build each user turn. The tab
 * builds the conversation and the Worker re-checks it, so both import these
 * from here rather than each keeping a copy that could drift.
 */
import { IRIS_SCRIPTED_THROUGH_LEVEL } from './iris-script.js';
/**
 * Where Iris starts proposing work from the model on the general track.
 *
 * Every level may be done by hand, by Iris, or by both
 * (docs/agent-first-plan.md). Up to IRIS_SCRIPTED_THROUGH_LEVEL her steps are
 * scripted, so the first levels cost nothing and need no account; from here
 * she is the live agent, which needs one, on every level that does not carry
 * a script of its own.
 */
export const IRIS_AGENT_FROM_LEVEL = IRIS_SCRIPTED_THROUGH_LEVEL + 1;
export function irisMode(m) {
    if (m.track === 'checkpoint') {
        // Designed agents first, for the general track's reason; a checkpoint
        // that has neither keeps no agent at all.
        if (m.scripted)
            return 'scripted';
        return typeof m.irisBudget === 'number' && m.irisBudget > 0 ? 'agent' : 'none';
    }
    if (m.track === 'general') {
        // A level with a script has designed agents whatever its number
        // (docs/designed-agents.md): the script wins over the live agent, and
        // the parser refuses a level that carries both.
        if (m.scripted)
            return 'scripted';
        // A level in the free stretch without its script yet keeps the mentor.
        return m.level >= IRIS_AGENT_FROM_LEVEL ? 'agent' : 'teach';
    }
    // The AI and security tracks are entered after the general track's first
    // half, so every level on them is past the point where Iris starts working.
    return 'agent';
}
/**
 * Which Iris the tab draws: her designed steps, or no Iris box at all
 * (owner, 2026-10-18).
 *
 * `irisMode` says what a level would get with model turns on, and the Worker
 * still asks it. In production they are off, and the tab cannot see that
 * switch, so it does not try: the rule is the level's own. Where Iris has
 * designed steps (general levels 0-19 and every checkpoint, today) she is
 * there; everywhere else there is no box, nothing saying she is missing, and
 * the hint ladder is the help. Bringing the live agent back is this one
 * function.
 */
export function irisPanel(m) {
    return irisMode(m) === 'scripted' ? 'scripted' : 'none';
}
/* ── Limits the Worker enforces and the tab respects ───────────────────── */
/**
 * Command output is cut before it goes back to the model. Every step resends
 * the whole conversation, so one `cat` of a large file is paid for on every
 * step after it; this cap is the biggest single lever on what a task costs.
 */
export const IRIS_OUTPUT_MAX = 4_000;
/** A learner's own words in one turn (a request, a rejection note). */
export const IRIS_NOTE_MAX = 1_000;
/** One turn of any kind, as the Worker accepts it. */
export const IRIS_TURN_MAX = 12_000;
/** Turns in one conversation, as the Worker accepts them. */
export const IRIS_TURNS_MAX = 100;
/**
 * The task as Iris is given it: the title, the brief, the visible objectives
 * and the constraints, which is what the learner reads on the screen. One
 * function, used by the tab (a level), the Worker (a checkpoint, from its own
 * catalogue) and scripts/iris/measure.mjs, so that what was measured is what
 * ships. A hidden objective is dropped here: its title is part of the
 * assessment, and Iris is told nothing the learner is not.
 */
export function irisBrief(m) {
    const visible = (m.objectives ?? []).filter((o) => !o.hidden).map((o) => `- ${o.title}`);
    const rules = (m.constraints ?? []).map((c) => `- ${c}`);
    return [
        `# ${m.title}`, '', m.brief.trim(),
        ...(visible.length ? ['', '## Objectives', ...visible] : []),
        ...(rules.length ? ['', '## Constraints', ...rules] : []),
    ].join('\n');
}
/* ── The user turns ─────────────────────────────────────────────────────── */
/** The first turn: the task as the learner sees it, and where the terminal starts. */
export function irisOpening(brief, cwd, request = '') {
    const asked = request.trim() ? `\n\nThe learner asks: ${request.trim().slice(0, IRIS_NOTE_MAX)}` : '';
    return `${brief.trim()}\n\n(The terminal starts in ${cwd}/.)${asked}`;
}
const cut = (s) => s.length > IRIS_OUTPUT_MAX ? `${s.slice(0, IRIS_OUTPUT_MAX)}\n…[${s.length - IRIS_OUTPUT_MAX} more characters cut]` : s;
/** An approved command's result. */
export function irisOutput(command, exit, stdout, stderr) {
    return [`$ ${command}`, cut(stdout), stderr ? `[stderr]\n${cut(stderr)}` : '', `[exit ${exit}]`]
        .filter(Boolean).join('\n');
}
/** The learner rejected the last step, with their reason or instruction if they gave one. */
export function irisRejected(note = '') {
    const said = note.trim().slice(0, IRIS_NOTE_MAX);
    return said ? `The learner rejected that step and says: ${said}` : 'The learner rejected that step.';
}
/** The learner wrote to Iris between steps, or after she said she was done. */
export function irisNote(note) {
    return `The learner says: ${note.trim().slice(0, IRIS_NOTE_MAX)}`;
}
/** What Check my work reported, as the learner saw it. */
export function irisCheck(summary) {
    return `Check my work:\n${cut(summary)}`;
}
/** The model's text as a step, or null when it is not one. */
export function parseIrisStep(text) {
    let raw;
    try {
        raw = JSON.parse(text);
    }
    catch {
        return null;
    }
    if (!raw || typeof raw.say !== 'string' || !raw.action || typeof raw.action.kind !== 'string')
        return null;
    const say = raw.say.trim();
    switch (raw.action.kind) {
        case 'run':
            return typeof raw.action.command === 'string' && raw.action.command.trim() !== ''
                ? { say, action: { kind: 'run', command: raw.action.command } } : null;
        case 'check': return { say, action: { kind: 'check' } };
        case 'done': return { say, action: { kind: 'done' } };
        default: return null;
    }
}
//# sourceMappingURL=iris.js.map